import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import { DashboardDataRaw, DateRange } from '../../types/Dashboard';
import { Panel, Badge, ShareBar } from '../ui';
import { planColor, formatCurrency, formatNumber, tooltipStyle } from './dashboardUi';
import {
  activesAtDate,
  exclusive8080AutoRevenue,
  lastDayOfMonth,
  manualOverrideForMonth,
} from './dashboardHelpers';

ChartJS.register(...registerables);

interface Props {
  data: DashboardDataRaw;
  range: DateRange;
}

const EXCLUSIVE_PLAN = 'Exclusive 8080';

/**
 * Total al centro de la dona. El agujero queda vacío y el dato más importante
 * del gráfico —cuántos son en total— tendría que leerse sin ir a la tabla.
 * Los tipos de plugin de Chart.js son laxos a propósito: `chart` trae el
 * contexto 2D ya escalado por devicePixelRatio.
 * El valor viaja en `options.plugins.centerTotal` y no en el cierre del plugin:
 * react-chartjs-2 registra los plugins solo al crear el gráfico, así que un
 * valor capturado ahí queda congelado cuando cambian los datos o el rango.
 */
export const centerTotalPlugin = {
  id: 'centerTotal',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  afterDatasetsDraw(chart: any, _args: unknown, opts: { value?: string; label?: string }) {
    const { ctx, chartArea } = chart;
    if (!chartArea || !opts.value) return;
    const { value, label = '' } = opts;
    const x = (chartArea.left + chartArea.right) / 2;
    const y = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#0f172a';
    ctx.font = "700 22px 'Nunito Sans', sans-serif";
    ctx.fillText(value, x, y - 7);
    ctx.fillStyle = '#94a3b8';
    ctx.font = "600 10px 'Nunito Sans', sans-serif";
    ctx.fillText(label.toUpperCase(), x, y + 13);
    ctx.restore();
  },
};

const th = 'pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400';

const PlanMixChart: React.FC<Props> = ({ data, range }) => {
  const { plans, totalCantidad, totalRevenue, exclusiveIsAuto } = useMemo(() => {
    const activesEnd = activesAtDate(data.premium, range.to);
    const snapshotEoM = lastDayOfMonth(range.to);
    const exclusiveOverride = manualOverrideForMonth(
      data.manualRevenueHistory[EXCLUSIVE_PLAN] ?? [],
      snapshotEoM
    );
    const exclusiveAuto = exclusive8080AutoRevenue(data.premium, snapshotEoM, EXCLUSIVE_PLAN);

    const plans = Object.entries(activesEnd).map(([plan, cantidad]) => {
      let revenue: number;
      let isManual = false;
      if (plan === EXCLUSIVE_PLAN) {
        if (exclusiveOverride) {
          revenue = exclusiveOverride.monthly_amount;
          isManual = true;
        } else {
          revenue = exclusiveAuto.revenue;
        }
      } else {
        revenue = cantidad * (data.precios[plan] ?? 0);
      }
      return { plan, cantidad, revenue, isManual };
    });

    // De mayor a menor: la tabla y la dona cuentan la misma historia en orden.
    plans.sort((a, b) => b.cantidad - a.cantidad);

    const totalCantidad = plans.reduce((s, p) => s + p.cantidad, 0);
    const totalRevenue = plans.reduce((s, p) => s + p.revenue, 0);
    const exclusiveIsAuto = !exclusiveOverride && activesEnd[EXCLUSIVE_PLAN] !== undefined;

    return { plans, totalCantidad, totalRevenue, exclusiveIsAuto };
  }, [data, range]);

  const chartData = {
    labels: plans.map((p) => p.plan),
    datasets: [
      {
        data: plans.map((p) => p.cantidad),
        backgroundColor: plans.map((p) => planColor(p.plan)),
        borderWidth: 3,
        borderColor: '#fff',
        hoverOffset: 6,
        hoverBorderColor: '#fff',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: { display: false },
      centerTotal: { value: formatNumber(totalCantidad), label: 'activos' },
      tooltip: {
        ...tooltipStyle,
        callbacks: {
          label: (ctx: { label: string; parsed: number }) => {
            const pct = totalCantidad > 0 ? (ctx.parsed / totalCantidad) * 100 : 0;
            return ` ${formatNumber(ctx.parsed)} suscriptores · ${pct.toFixed(1)}%`;
          },
        },
      },
    },
  };

  return (
    <Panel
      title="Mix de planes premium"
      subtitle="Reparto de la cartera al cierre del rango, por cantidad y por lo que aporta cada plan."
    >
      <div className="flex flex-col items-start gap-6 lg:flex-row">
        <div className="mx-auto h-52 w-52 shrink-0 lg:mx-0">
          <Doughnut
            data={chartData}
            options={options}
            plugins={[centerTotalPlugin]}
          />
        </div>

        <div className="-mx-6 min-w-0 flex-1 overflow-x-auto px-6">
          <table className="w-full min-w-[460px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className={`${th} pe-4`}>Plan</th>
                <th className={`${th} pe-4 text-right`}>Activos</th>
                <th className={`${th} pe-4 w-28`}>Mix</th>
                <th className={`${th} pe-4 text-right`}>Revenue/mes</th>
                <th className={`${th} text-right`}>% rev.</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((row) => {
                const pctMix = totalCantidad > 0 ? (row.cantidad / totalCantidad) * 100 : 0;
                const pctRev = totalRevenue > 0 ? (row.revenue / totalRevenue) * 100 : null;
                return (
                  <tr
                    key={row.plan}
                    className="border-b border-slate-100 transition-colors duration-100 ease-out last:border-0 hover:bg-slate-50/70"
                  >
                    <td className="py-2 pe-4">
                      <span className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: planColor(row.plan) }}
                        />
                        <span className="font-medium text-gappex-secondary">{row.plan}</span>
                        {row.plan === EXCLUSIVE_PLAN && (
                          <Badge
                            tone={row.isManual ? 'warning' : 'positive'}
                            title={
                              row.isManual
                                ? 'Revenue cargado a mano para este mes'
                                : 'Revenue estimado a partir de la edad de cada suscriptor'
                            }
                          >
                            {row.isManual ? 'manual' : 'auto'}
                          </Badge>
                        )}
                      </span>
                    </td>
                    <td className="py-2 pe-4 text-right font-semibold tabular-nums text-gappex-secondary">
                      {formatNumber(row.cantidad)}
                    </td>
                    <td className="py-2 pe-4">
                      <ShareBar pct={pctMix} color={planColor(row.plan)} />
                      <span className="mt-1 block text-[11px] tabular-nums text-slate-400">
                        {pctMix.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-2 pe-4 text-right tabular-nums text-slate-600">
                      {formatCurrency(row.revenue)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-slate-500">
                      {pctRev === null ? '—' : `${pctRev.toFixed(1)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 font-semibold text-gappex-secondary">
                <td className="pt-3 pe-4">Total</td>
                <td className="pt-3 pe-4 text-right tabular-nums">{formatNumber(totalCantidad)}</td>
                <td className="pt-3 pe-4 text-[11px] tabular-nums text-slate-400">100%</td>
                <td className="pt-3 pe-4 text-right tabular-nums">{formatCurrency(totalRevenue)}</td>
                <td className="pt-3 text-right tabular-nums text-slate-500">100%</td>
              </tr>
            </tfoot>
          </table>

          {exclusiveIsAuto && (
            <p className="mt-3 text-[11px] text-slate-400">
              El revenue de {EXCLUSIVE_PLAN} sale de la edad de cada suscriptor activo: es una
              estimación, no un valor exacto.
            </p>
          )}
        </div>
      </div>
    </Panel>
  );
};

export default PlanMixChart;
